import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { fetchServiceHistory, HistoryYear, HistorySelection } from '../../../../services/historyService';
import { ServiceSelection } from '../../types/planningTypes';

interface UseAutofillFromHistoryProps {
  liturgyName: string;
  churchId?: number;
  serviceId: number;
  currentSelections: ServiceSelection[];
  onAppendSelections: (newSelections: ServiceSelection[]) => void;
}

interface UseAutofillFromHistoryReturn {
  availableYears: HistoryYear[];
  isLoading: boolean;
  error: string | null;
  applyYearSelections: (yearKey: string) => void;
}

export const useAutofillFromHistory = ({
  liturgyName,
  churchId,
  serviceId,
  currentSelections,
  onAppendSelections
}: UseAutofillFromHistoryProps): UseAutofillFromHistoryReturn => {
  const [availableYears, setAvailableYears] = useState<HistoryYear[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch history when liturgyName changes
  useEffect(() => {
    if (!liturgyName) return;

    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchServiceHistory(liturgyName, churchId, false);
        // Filter to only years that have song selections (not current year, and has songs)
        const yearsWithSongs = data.years.filter(
          year => !year.isCurrent && year.songSelections.length > 0
        );
        setAvailableYears(yearsWithSongs);
      } catch (err) {
        setError('Failed to load history');
        console.error('Error loading history for autofill:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [liturgyName, churchId]);

  const applyYearSelections = useCallback((yearKey: string) => {
    const selectedYear = availableYears.find(y => y.liturgicalYear === yearKey);
    if (!selectedYear) return;

    // Filter out selections where the song no longer exists (songId is null/undefined)
    const validSelections = selectedYear.songSelections.filter(
      (sel: HistorySelection) => sel.songId != null
    );

    if (validSelections.length === 0) {
      toast.warning('No valid songs found in selected year');
      return;
    }

    if (validSelections.length < selectedYear.songSelections.length) {
      const skipped = selectedYear.songSelections.length - validSelections.length;
      toast.info(`${skipped} song(s) skipped (no longer in database)`);
    }

    // Calculate max order_number from current selections
    const maxOrder = currentSelections.length > 0
      ? Math.max(...currentSelections.map(s => s.order_number))
      : 0;

    // Transform history selections to new ServiceSelection objects
    const newSelections: ServiceSelection[] = validSelections.map(
      (histSel: HistorySelection, index: number) => ({
        // No service_selection_id - this is a new record
        service_id: serviceId,
        song_id: histSel.songId,
        version_id: histSel.versionId,
        placement: histSel.songPlacement,
        order_number: maxOrder + index + 1,
        song_title: histSel.songTitle,
        composer_name: histSel.composerName || undefined,
        version_title: histSel.versionTitle,
        performance_type: histSel.performanceType,
        is_template_music: false
      })
    );

    onAppendSelections(newSelections);

    const yearLabel = selectedYear.yearLetter
      ? `Year ${selectedYear.yearLetter}`
      : selectedYear.liturgicalYear;
    toast.success(`Added ${newSelections.length} song(s) from ${yearLabel}`);
  }, [availableYears, currentSelections, serviceId, onAppendSelections]);

  return {
    availableYears,
    isLoading,
    error,
    applyYearSelections
  };
};
